import { Injectable, inject } from '@angular/core';
import { Company } from 'src/app/shared/models/company.model';
import { BehaviorSubject, firstValueFrom, Observable, Subject } from 'rxjs';
import { environment } from 'src/environments/environment';
import { HttpClient } from '@angular/common/http';
import { SocketService } from './socket.service';
/**
 * Service for company
 */
/** GRC tab refreshed by a socket signal */
export type GrcSection = 'governance' | 'usage';

@Injectable({ providedIn: 'root' })
/**
 * CompanyService
 * @class
 */
export class CompanyService {
  /**
   * inject http
   * @param {HttpClient} http
   */
  private http = inject(HttpClient);
  /**
   * inject SocketService
   * @param {SocketService} socketService
   */
  private socketService = inject(SocketService);
  /**
   * baseApiUrl
   * @type {string}
   */
  private baseApiUrl: string = environment.apiUrl + 'company';
  /**
   * currentCompany$
   * @type {BehaviorSubject<Company>}
   */
  private currentCompany$: BehaviorSubject<Company> = new BehaviorSubject(null);
  /**
   * echoInstance
   * @type {any}
   */
  private echoInstance = null;
  /**
   * listGroup
   * @type {string[]}
   */
  private listGroup = [];
  /**
   * listSystems
   * @type {Isystem[]}
   */
  private listSystems: Isystem[] = [];
  /**
   * listFolder
   * @type {string[]}
   */
  private listFolder = [];
  /**
   * socketCharts$
   * @type {Subject<IChartsUpdate>}
   */
  private socketCharts$: Subject<IChartsUpdate> = new Subject();
  /**
   * socketParams$
   * @type {Subject<IParamsUpdate>}
   */
  private socketParams$: Subject<IParamsUpdate> = new Subject();
  /**
   * socketListGroup$
   * @type {BehaviorSubject<string[]>}
   */
  private socketListGroup$: BehaviorSubject<string[]> = new BehaviorSubject([]);
  /**
   * GRC tab whose numbers changed on the server ('governance' | 'usage')
   * @type {Subject<GrcSection>}
   */
  private socketGrc$: Subject<GrcSection> = new Subject();
  /**
   * socketListSystems$
   * @type {BehaviorSubject<Isystem[]>}
   */
  private socketListSystems$: BehaviorSubject<Isystem[]> = new BehaviorSubject([]);
  /**
   * socketListFolder$
   * @type {BehaviorSubject<string[]>}
   */
  private socketListFolder$: BehaviorSubject<string[]> = new BehaviorSubject([]);
  /**
   * socketRefreshGroup$
   * @type {Subject<any>}
   */
  private socketRefreshGroup$: Subject<any> = new Subject();
  /**
   * socketLog$
   * @type {Subject<any>}
   */
  private socketLog$: Subject<any> = new Subject();
  /**
   * socketRefreshAd$
   * @type {Subject<void>}
   */
  private socketRefreshAd$: Subject<void> = new Subject();

  /**
   * Initializes the socket connection for the company service and sets up event listeners.
   * Fetches the group and folder lists and updates the respective subjects with the data.
   * Sets up socket listeners to handle various company-related events including:
   * - SeatsUpdatedEvent: Updates the current company information.
   * - GroupAddedEvent: Adds new groups to the list and notifies subscribers.
   * - GroupDeletedEvent: Removes groups from the list and notifies subscribers.
   * - FolderAddedEvent: Adds new folders to the list and notifies subscribers.
   * - FolderDeletedEvent: Removes folders from the list and notifies subscribers.
   * - CompanyUpdatedEvent: Updates the current company information.
   * - CompanyAddLogEvent: Triggers a log update.
   * - GroupRefreshEvent: Refreshes the group information.
   * - RefreshActiveDirectoryEvent: Refreshes the Active Directory information.
   */
  startSocket() {
    Promise.all([this.getGroupList(),
    this.getFolderList()
    ])
      .then(([suc1, suc2]) => {
        if (suc1.success === 1 && suc2.success === 1) {
          this.listGroup = suc1.group;
          this.listFolder = suc2.folders;
          this.socketListFolder$.next(this.listFolder);
          this.socketListGroup$.next(this.listGroup);
          this.socketListSystems$.next(this.listSystems);
        }
      })
      .catch(() => { })
      .finally(() => {
        this.echoInstance = this.socketService.getEchoInstance();
        this.echoInstance.private(`company.${this.socketService.companyId}`)
          .listen('.SeatsUpdatedEvent', (event) => {
            this.setCurrentCompany(new Company(event.company));
          })
          .listen('.GroupAddedEvent', (event) => {
            this.listGroup = this.listGroup.concat(event.group);
            this.socketListGroup$.next(this.listGroup);
          })
          .listen('.GroupDeletedEvent', (event) => {
            this.listGroup = this.listGroup.filter((item) => !event.groups.includes(item));
            this.socketListGroup$.next(this.listGroup);
          })
          .listen('.FolderAddedEvent', (event) => {
            this.listFolder = this.listFolder.concat(event.folder);
            this.socketListFolder$.next(this.listFolder);
          })
          .listen('.FolderDeletedEvent', (event) => {
            this.listFolder = this.listFolder.filter((item) => event.folder !== item);
            this.socketListFolder$.next(this.listFolder);
          })
          .listen('.CompanyUpdatedEvent', (event) => {
            this.setCurrentCompany(new Company(event.companyWeb));
          })
          .listen('.CompanyAddLogEvent', (event) => {
            this.socketLog$.next(event);
          })
          .listen('.GroupRefreshEvent', () => {
            this.socketRefreshGroup$.next(null);
          })
          .listen('.RefreshActiveDirectoryEvent', () => {
            this.socketRefreshAd$.next(null);
          });
      });
  }

  /**
   * If the list of all systems is empty, fetches the list of systems.
   * Stores the result in the class property listSystems.
   * @returns {void}
   */
  getAllSystemList() {
    if (this.listSystems.length === 0) {
      this.getSystemList()
        .then((suc) => {
          if (suc.success === 1) {
            this.listSystems = suc.systems;
          }
        })
        .catch(() => { });
    }
  }

  /**
   * Subscribes to Company.*Events in order to update the charts in the company page.
   * The socketCharts$ observable is updated with the new values.
   * The keys are 'seats', 'users', 'password', 'passwordDetails', 'creditCardsDetails', 'accessKitsDetails'.
   * The values are the new values for the respective keys.
   */
  startChartsSocket() {
    this.echoInstance = this.socketService.getEchoInstance();
    // Leave first to prevent duplicate listeners if called multiple times
    this.echoInstance.leave(`company.${this.socketService.companyId}.charts`);
    this.echoInstance.private(`company.${this.socketService.companyId}.charts`)
      .listen('.CompanyTotalSeatsEvent', (event) => {
        this.socketCharts$.next({ key: 'seats', value: event.totalSeats });
      })
      .listen('.CompanyRegistredUsersEvent', (event) => {
        this.socketCharts$.next({ key: 'users', value: event.RegistredUsers });
      })
      .listen('.CompanyTotalPasswordsEvent', (event) => {
        this.socketCharts$.next({ key: 'password', value: event.totalPasswords });
      })
      .listen('.CompanyPasswordsDetailsEvent', (event) => {
        this.socketCharts$.next({ key: 'passwordDetails', value: event.passwordDetails });
      })
      .listen('.CompanyCreditCardsDetailsEvent', (event) => {
        this.socketCharts$.next({ key: 'creditCardsDetails', value: event.creditCardsDetails });
      })
      .listen('.CompanyAccessKitsDetailsEvent', (event) => {
        this.socketCharts$.next({ key: 'accessKitsDetails', value: event.accessKitsDetails });
      });
  }

  /**
   * Subscribes to Company.*Events in order to update the company's parameters in the company page.
   * The socketParams$ observable is updated with the new values.
   * The keys are 'password', 'frequency', 'level', 'ad', 'pin', 'pinFrequency', 'lockpatternFrequency', 'passphraseFrequency', 'automaticUpdate', 'copyTimeout', 'pinVerify', 'deviceRestriction'.
   * The values are the new values for the respective keys.
   */
  startParamsSocket() {
    this.echoInstance = this.socketService.getEchoInstance();
    // Leave first to prevent duplicate listeners if called multiple times
    this.echoInstance.leave(`company.${this.socketService.companyId}.company`);
    this.echoInstance.private(`company.${this.socketService.companyId}.company`)
      .listen('.CompanyPasswordStrengthEvent', (event) => {
        this.socketParams$.next({ key: 'password', value: event.passwordStrength });
      })
      .listen('.CompanyPasswordFrequencyEvent', (event) => {
        this.socketParams$.next({ key: 'frequency', value: { value: event.passwordTimers, status: event.statusPasswordTimers } });
      })
      .listen('.CompanyLevelAppEvent', (event) => {
        // tslint:disable-next-line: max-line-length
        this.socketParams$.next({ key: 'level', value: event.authorizedLevels.levelGold && event.authorizedLevels.levelSilver && event.authorizedLevels.levelBronze ? 1 : event.authorizedLevels.levelSilver && event.authorizedLevels.levelGold ? 2 : 3 });
      })
      .listen('.CompanyAdFrequencyEvent', (event) => {
        this.socketParams$.next({ key: 'ad', value: { value: event.adTimers, status: event.statusAdTimers } });
      })
      .listen('.CompanyPinLengthEvent', (event) => {
        this.socketParams$.next({ key: 'pin', value: { value: event.pinLength, status: event.statusPinLength } });
      })
      .listen('.CompanyPinFrequencyEvent', (event) => {
        this.socketParams$.next({ key: 'pinFrequency', value: { value: event.pinTimers, status: event.statusPinTimers } });
      })
      .listen('.CompanyLockpatternFrequencyEvent', (event) => {
        this.socketParams$.next({ key: 'lockpatternFrequency', value: { value: event.lockpatternTimers, status: event.statusLockpatternTimers } });
      })
      .listen('.CompanyPassphraseFrequencyEvent', (event) => {
        this.socketParams$.next({ key: 'passphraseFrequency', value: { value: event.passphraseTimers, status: event.statusPassphraseTimers } });
      })
      .listen('.CompanyAutomaticUpdateEvent', (event) => {
        this.socketParams$.next({ key: 'automaticUpdate', value: { status: event.statusAutomaticUpdate } });
      })
      .listen('.CompanyCopyTimeoutEvent', (event) => {
        this.socketParams$.next({ key: 'copyTimeout', value: { value: event.copyTimeout } });
      })
      .listen('.CompanyPinVerifyEvent', (event) => {
        this.socketParams$.next({ key: 'pinVerify', value: { value: event.pinVerify, status: event.statusPinVerify } });
      })
      .listen('.CheckDeviceRestrictionEvent', (event) => {
        this.socketParams$.next({
          key: 'deviceRestriction',
          value: {
            status: event.statusDeviceRestriction,
            applyToOwner: event.applyToOwner,
            applyToSuperAdmin: event.applyToSuperAdmin
          }
        });
      });
  }

  /**
   * Stops listening to the socket for the company.
   * Clears the local state of the lists (listGroup, listSystems, listFolder) and notifies the subscribers that the lists are empty.
   */
  stopSocket() {
    if (this.echoInstance) {
      this.echoInstance.leave(`company.${this.socketService.companyId}`);
      this.listGroup = [];
      this.listSystems = [];
      this.listFolder = [];
      this.socketListGroup$.next(this.listGroup);
      this.socketListSystems$.next(this.listSystems);
      this.socketListFolder$.next(this.listFolder);
    }
  }

  /**
   * Listens to the GRC refresh signals of the company (no data: the tabs reload through the GRC endpoints).
   * PasswordTrackingRefreshEvent: Access governance numbers changed; AppUsageRefreshEvent: new app activity.
   */
  startGrcSocket() {
    this.echoInstance = this.socketService.getEchoInstance();
    // Leave first to prevent duplicate listeners if called multiple times
    this.echoInstance.leave(`company.${this.socketService.companyId}.grc`);
    this.echoInstance.private(`company.${this.socketService.companyId}.grc`)
      .listen('.PasswordTrackingRefreshEvent', () => this.socketGrc$.next('governance'))
      .listen('.AppUsageRefreshEvent', () => this.socketGrc$.next('usage'));
  }

  /**
   * Stops listening to the GRC refresh signals of the company.
   */
  stopGrcSocket() {
    if (this.echoInstance) {
      this.echoInstance.leave(`company.${this.socketService.companyId}.grc`);
    }
  }

  /**
   * Emits the GRC tab whose numbers changed on the server.
   * @returns {Observable<GrcSection>}
   */
  subSocketGrc(): Observable<GrcSection> {
    return this.socketGrc$;
  }

  /**
   * Stops listening to the charts socket for the company.
   * Leaves the private channel associated with company charts.
   */
  stopChartsSocket() {
    if (this.echoInstance) {
      this.echoInstance.leave(`company.${this.socketService.companyId}.charts`);
    }
  }

  /**
   * Stops listening to the socket for company parameters.
   * Leaves the private channel associated with the company parameters.
   */
  stopParamsSocket() {
    if (this.echoInstance) {
      this.echoInstance.leave(`company.${this.socketService.companyId}.company`);
    }
  }

  /**
   * Returns an observable that emits values when the company parameters are updated.
   * the values are of type IParamsUpdate, which is an object with a key and a value.
   * The key is one of the following: 'password', 'frequency', 'level', 'ad', 'pin', 'pinFrequency', 'lockpatternFrequency', 'passphraseFrequency', 'automaticUpdate', 'copyTimeout', 'pinVerify', 'deviceRestriction'.
   * the value is the new value for the respective key.
   * @returns {Observable<IParamsUpdate>} 
   */
  subSocketParams(): Observable<IParamsUpdate> {
    return this.socketParams$;
  }

  /**
   * Returns an observable that emits values when the company charts are updated.
   * The values are of type IChartsUpdate, which is an object with a key and a value.
   * The key is one of the following: 'seats', 'users', 'password', 'passwordDetails', 'creditCardsDetails', 'accessKitsDetails'.
   * the value is the new value for the respective key.
   * @returns {Observable<IChartsUpdate>} 
   */
  subSocketCharts(): Observable<IChartsUpdate> {
    return this.socketCharts$;
  }

  /**
   * Returns an observable that emits values when the list of groups is updated.
   * The values are arrays of strings, each string being the name of a group.
   * @returns {Observable<string[]>}
   */
  subSocketListGroup(): Observable<string[]> {
    return this.socketListGroup$;
  }

  /**
   * Returns an observable that emits values when the list of systems is updated.
   * The values are arrays of Isystem objects, each object containing the name, id, and type of a system.
   * @returns {Observable<Isystem[]>}
   */
  subSocketListSystem(): Observable<Isystem[]> {
    return this.socketListSystems$;
  }

  /**
   * Returns an observable that emits values when the list of folders is updated.
   * The values are arrays of strings, each string being the name of a folder.
   * @returns {Observable<string[]>}
   */
  subSocketListFolder(): Observable<string[]> {
    return this.socketListFolder$;
  }
  /**
   * Returns an observable that emits values when the list of groups needs to be refreshed.
   * The values emitted are undefined.
   * @returns {Observable<any>} 
   */
  subSocketRefreshGroup(): Observable<any> {
    return this.socketRefreshGroup$;
  }

  /**
   * Returns an observable that emits values when a log is added or updated.
   * The values emitted are of type ILog, which contains the log data.
   * @returns {Observable<ILog>}
   */
  subSocketLog(): Observable<any> {
    return this.socketLog$;
  }

  /**
   * Returns an observable that emits values when the active directory needs to be refreshed.
   * The values emitted are undefined.
   * @returns {Observable<void>}
   */
  subSocketRefreshAd(): Observable<void> {
    return this.socketRefreshAd$;
  }

  /**
   * Updates the current company and optionally checks the system list based on the company's addons.
   * 
   * @param company - The new company object to set as the current company.
   * @param checkSystemList - A boolean indicating whether to check and update the system list based on company's autoUpdatePreloadPassword addon.
   *                          Defaults to true.
   * If 'checkSystemList' is true, it checks the 'autoUpdatePreloadPassword' addon:
   * - Clears 'listSystems' if the current company has the addon and the new company does not.
   * - Calls 'getAllSystemList()' if the current company does not have the addon and the new company does.
   * 
   * Updates the 'currentCompany$' observable with the new company.
   */
  setCurrentCompany(company: Company, checkSystemList = true): void {
    if (checkSystemList) {
      if (this.currentCompany$.value?.addons.autoUpdatePreloadPassword && !company?.addons.autoUpdatePreloadPassword) {
        this.listSystems = [];
      } else if (!this.currentCompany$.value?.addons.autoUpdatePreloadPassword && company?.addons.autoUpdatePreloadPassword) {
        this.getAllSystemList();
      }
    }
    this.currentCompany$.next(company);
  }

  /**
   * Returns an observable that emits the current company object.
   * Subscribers will receive updates whenever the current company is changed.
   * @returns {Observable<Company>} An observable emitting the current company.
   */
  SubCurrentCompany(): Observable<Company> {
    return this.currentCompany$;
  }
  /**
   * Gets the current company from the server.
   * @returns {Promise<any>} A promise that resolves to the current company object.
   */
  getCurrentCompany(): Promise<any> {
    return firstValueFrom(this.http.get(this.baseApiUrl));
  }
  /**
   * Gets the current password strength requirement from the server.
   * @returns {Promise<any>} A promise that resolves to the current password strength requirement.
   */

  getPasswordStrength(): Promise<any> {
    return firstValueFrom(this.http.get(this.baseApiUrl + '/parameter/password/strength'));
  }

  /**
   * Updates the current password strength requirement.
   * @param data The data to send to the server, which should contain the new password strength requirement.
   * @returns {Promise<any>} A promise that resolves when the request is complete.
   */
  updatePasswordStrength(data): Promise<any> {
    return firstValueFrom(this.http.post(this.baseApiUrl + '/parameter/password/strength', data));
  }

  /**
   * Gets the current password timer from the server.
   * @returns {Promise<any>} A promise that resolves to the current password timer.
   */
  getPasswordTimer(): Promise<any> {
    return firstValueFrom(this.http.get(this.baseApiUrl + '/parameter/password/timer'));
  }

  /**
   * Gets the current Active Directory timer from the server.
   * @returns {Promise<any>} A promise that resolves to the current Active Directory timer.
   */
  getAdTimer(): Promise<any> {
    return firstValueFrom(this.http.get(this.baseApiUrl + '/parameter/ad/timer'));
  }

  /**
   * Updates the current password timer.
   * @param data The data to send to the server, which should contain the new password timer.
   * @returns {Promise<any>} A promise that resolves when the request is complete.
   */
  updatePasswordTimer(data): Promise<any> {
    return firstValueFrom(this.http.post(this.baseApiUrl + '/parameter/password/timer', data));
  }

  /**
   * Updates the current Active Directory timer.
   * @param data The data to send to the server, which should contain the new Active Directory timer.
   * @returns {Promise<any>} A promise that resolves when the request is complete.
   */
  updateAdTimer(data): Promise<any> {
    return firstValueFrom(this.http.post(this.baseApiUrl + '/parameter/ad/timer', data));
  }

  /**
   * Retrieves the  level from the server.
   * @returns {Promise<any>} A promise that resolves to the level.
   */
  getlevel(): Promise<any> {
    return firstValueFrom(this.http.get(this.baseApiUrl + '/parameter/level'));
  }

  /**
   * Updates the level.
   * @param data The data to send to the server, which should contain the new level.
   * @returns {Promise<any>} 
   */
  updatelevel(data): Promise<any> {
    return firstValueFrom(this.http.post(this.baseApiUrl + '/parameter/level', data));
  }

  /**
   * Gets the current pin strength requirement from the server.
   * @returns {Promise<any>} 
   */
  getPinStrength(): Promise<any> {
    return firstValueFrom(this.http.get(this.baseApiUrl + '/parameter/pin/length'));
  }

  /**
   * Updates the current pin strength requirement.
   * @param data The data to send to the server, which should contain the new pin strength requirement.
   * @returns {Promise<any>}
   */
  updateCompanyPin(data): Promise<any> {
    return firstValueFrom(this.http.post(this.baseApiUrl + '/parameter/pin/length', data));
  }

  /**
   * Gets the current pin frequency from the server.
   * @returns {Promise<any>} 
   */
  getPinFrequency(): Promise<any> {
    return firstValueFrom(this.http.get(this.baseApiUrl + '/parameter/pin/timer'));
  }

  /**
   * Updates the current pin frequency.
   * @param data The data to send to the server, which should contain the new pin frequency.
   * @returns {Promise<any>} 
   */
  updatePinFrequency(data): Promise<any> {
    return firstValueFrom(this.http.post(this.baseApiUrl + '/parameter/pin/timer', data));
  }

  /**
   * Gets the current pin verify requirement from the server.
   * @returns {Promise<any>} 
   */
  getPinVerify(): Promise<any> {
    return firstValueFrom(this.http.get(this.baseApiUrl + '/parameter/pin/verify'));
  }

  /**
   * Updates the current pin verify requirement.
   * @param data The data to send to the server, which should contain the new pin verify requirement.
   * @returns {Promise<any>} 
   */
  updatePinVerify(data): Promise<any> {
    return firstValueFrom(this.http.post(this.baseApiUrl + '/parameter/pin/verify', data));
  }

  /**
   * Gets the current lockpattern frequency requirement from the server.
   * @returns {Promise<any>}
   */
  getLockpatternFrequency(): Promise<any> {
    return firstValueFrom(this.http.get(this.baseApiUrl + '/parameter/lockpattern/timer'));
  }

  /**
   * Updates the current lockpattern frequency.
   * @param data The data to send to the server, which should contain the new lockpattern frequency.
   * @returns {Promise<any>} 
   */
  updateLockpatternFrequency(data): Promise<any> {
    return firstValueFrom(this.http.post(this.baseApiUrl + '/parameter/lockpattern/timer', data));
  }

  /**
   * Gets the current passphrase frequency from the server.
   * @returns {Promise<any>} 
   */
  getPassphraseFrequency(): Promise<any> {
    return firstValueFrom(this.http.get(this.baseApiUrl + '/parameter/passphrase/timer'));
  }

  /**
   * Updates the current passphrase frequency.
   * @param data The data to send to the server, which should contain the new passphrase frequency.
   * @returns {Promise<any>} 
   */
  updatePassphraseFrequency(data): Promise<any> {
    return firstValueFrom(this.http.post(this.baseApiUrl + '/parameter/passphrase/timer', data));
  }

  /**
   * Gets the current automatic update configuration from the server.
   * @returns {Promise<any>} 
   */
  getAutomaticUpdate(): Promise<any> {
    return firstValueFrom(this.http.get(this.baseApiUrl + '/parameter/auto-update'));
  }

  /**
   * Updates the current automatic update configuration.
   * @param data The data to send to the server, which should contain the new automatic update configuration.
   * @returns {Promise<any>}
   */
  updateAutomaticUpdate(data): Promise<any> {
    return firstValueFrom(this.http.post(this.baseApiUrl + '/parameter/auto-update', data));
  }

  /**
   * Gets the current copy timeout from the server.
   * @returns {Promise<any>}
   */
  getCopyTimeout(): Promise<any> {
    return firstValueFrom(this.http.get(this.baseApiUrl + '/parameter/copy-timeout'));
  }

  /**
   * Updates the current copy timeout.
   * @param data The data to send to the server, which should contain the new copy timeout.
   * @returns {Promise<any>} 
   */
  updateCopyTimeout(data): Promise<any> {
    return firstValueFrom(this.http.post(this.baseApiUrl + '/parameter/copy-timeout', data));
  }

  /**
   * Fetches the device restriction settings from the server.
   * @returns {Promise<any>}
   */
  getDeviceRestriction(): Promise<any> {
    return firstValueFrom(this.http.get(this.baseApiUrl + '/parameter/device-restriction'));
  }

  /**
   * Updates the device restriction settings on the server.
   * @param data The data to send to the server, which should contain the new device restriction settings.
   * @returns {Promise<any>}
   */
  updateDeviceRestriction(data): Promise<any> {
    return firstValueFrom(this.http.post(this.baseApiUrl + '/parameter/device-restriction', data));
  }

  /**
   * Notifies the server that the user has opened the GRC page.
   * @returns {Promise<any>}
   */
  openGrc(): Promise<any> {
    return firstValueFrom(this.http.get(this.baseApiUrl + '/openGrc'));
  }

  /**
   * Gets the total number of passwords from the server.
   * @returns {Promise<any>}
   */
  getChartTotalPassword(): Promise<any> {
    return firstValueFrom(this.http.get(this.baseApiUrl + '/total-passwords'));
  }

  /**
   * Fetches the details of passwords from the server.
   * @returns {Promise<any>}
   */
  getPasswordDetails(): Promise<any> {
    return firstValueFrom(this.http.get(this.baseApiUrl + '/password-details'));
  }

  /**
   * Gets the user activity from the server.
   * @returns {Promise<any>}
   */
  getUsersActivity(): Promise<any> {
    return firstValueFrom(this.http.get(this.baseApiUrl + '/users-activity'));
  }

  /**
   * Fetches the total number of registered users from the server.
   * @returns {Promise<any>}
   */
  getChartregisteredUser(): Promise<any> {
    return firstValueFrom(this.http.get(this.baseApiUrl + '/registered-users'));
  }

  /**
   * Fetches the total number of seats from the server.
   * @returns {Promise<any>}
   */
  getChartTotalSeats(): Promise<any> {
    return firstValueFrom(this.http.get(this.baseApiUrl + '/total-seats'));
  }

  /**
   * Fetches the credit card details from the server.
   * @returns {Promise<any>}
   */
  getCreditCardsDetails(): Promise<any> {
    return firstValueFrom(this.http.get(this.baseApiUrl + '/credit-card-details'));
  }

  /**
   * Fetches the details of access kits from the server.
   * @returns {Promise<any>}
   */
  getAccessKitsDetails(): Promise<any> {
    return firstValueFrom(this.http.get(this.baseApiUrl + '/access-kit-details'));
  }

  /**
   * Updates the language of the console.
   * @param lang The language to switch to (e.g. 'en', 'fr', etc.)
   * @returns {Promise<any>}
   */
  updateLangConsole(lang): Promise<any> {
    return firstValueFrom(this.http.put(this.baseApiUrl + '/lang', { lang }));
  }

  /**
   * Gets the list of groups from the server.
   * @returns {Promise<any>} The list of groups, or an error if the request fails.
   */
  getGroupList(): Promise<any> {
    return firstValueFrom(this.http.get(this.baseApiUrl + '/group'));
  }

  /**
   * Fetches the list of systems from the server.
   * @returns {Promise<any>} The list of systems, or an error if the request fails.
   */
  getSystemList(): Promise<any> {
    return firstValueFrom(this.http.get(this.baseApiUrl + '/preloadPassword/systems'));
  }

  /**
   * Fetches the list of folders from the server.
   * @returns {Promise<any>} The list of folders, or an error if the request fails.
   */
  getFolderList(): Promise<any> {
    return firstValueFrom(this.http.get(this.baseApiUrl + '/folders'));
  }

  /**
   * Gets the list of users for a given group from the server.
   * @param name The name of the group to fetch users for.
   * @returns {Promise<any>} The list of users, or an error if the request fails.
   */
  getUsersForGroup(name): Promise<any> {
    return firstValueFrom(this.http.get(this.baseApiUrl + '/group/users', { params: name }));
  }

  /**
   * Gets the list of logs from the server.
   * @param filters The filters to apply to the log list.
   * @param token The token to use to authenticate the request.
   * @returns {Promise<any>} The list of logs, or an error if the request fails.
   */
  getLog(filters): Promise<any> {
    return firstValueFrom(this.http.get(this.baseApiUrl + '/logs', { params: filters }));
  }

  /**
   * Gets the chart data for the logs from the server.
   * @returns {Promise<any>} The chart data, or an error if the request fails.
   */
  getLogsChart(): Promise<any> {
    return firstValueFrom(this.http.get(this.baseApiUrl + '/logs/chart'));
  }

  /** Access governance overview of the current company */
  getCredentialDistribution(): Observable<any> {
    return this.http.get(this.baseApiUrl + '/credential-distribution');
  }

  /** Daily credential changes over a period */
  getCredentialChanges(params: { from: string }): Observable<any> {
    return this.http.get(this.baseApiUrl + '/credential-distribution/changes', { params });
  }

  /** Users and the credentials they hold */
  getCredentialUsers(params: Record<string, string | number>): Observable<any> {
    return this.http.get(this.baseApiUrl + '/credential-distribution/users', { params });
  }

  /** Users above the client's own policy limit, per day */
  getCredentialThreshold(params: { from: string; above: number }): Observable<any> {
    return this.http.get(this.baseApiUrl + '/credential-distribution/threshold', { params });
  }

  /** App usage of the current company over a period: adoption per day and totals */
  getAppUsage(params: { from: string; to: string }): Observable<any> {
    return this.http.get(this.baseApiUrl + '/app-usage', { params });
  }

  /** Users and their app usage over a period */
  getAppUsageUsers(params: Record<string, string | number>): Observable<any> {
    return this.http.get(this.baseApiUrl + '/app-usage/users', { params });
  }

  /** One user's app usage per day */
  getAppUsageUserDays(id: number, params: { from: string; to: string }): Observable<any> {
    return this.http.get(this.baseApiUrl + '/app-usage/users/' + id + '/days', { params });
  }

  /**
   * Validates the given password data against the server.
   * @param data The data object containing the password details to validate.
   * @returns {Promise<any>} A promise of the server's response indicating the validity of the password.
   */
  validatePassword(data): Promise<any> {
    return firstValueFrom(this.http.post(this.baseApiUrl + '/passwordvalidation', data));
  }

  /**
   * Requests the server to export logs based on the given parameters.
   * @param data The object containing the parameters to export logs.
   * @returns {Promise<any>}
   */
  sendExportLogsRequest(data): Promise<any> {
    return firstValueFrom(this.http.post(this.baseApiUrl + '/logs/export', data));
  }

  /**
   * Gets the current inactivity report settings from the server.
   * @returns {Promise<any>} A promise that resolves to the current inactivity report settings.
   */
  getInactivityReportSettings(): Promise<any> {
    return firstValueFrom(this.http.get(this.baseApiUrl + '/parameter/inactivity'));
  }

  /**
   * Updates the inactivity report settings.
   * @param data The data to send to the server, which should contain the new inactivity report settings.
   * @returns {Promise<any>} A promise that resolves when the request is complete.
   */
  updateInactivityReportSettings(data): Promise<any> {
    return firstValueFrom(this.http.post(this.baseApiUrl + '/parameter/inactivity', data));
  }

  /**
   * Sends verification code to email for inactivity report recipients.
   * @param email The email address to send verification code to.
   * @returns {Promise<any>} A promise that resolves to the API response.
   */
  sendInactivityEmailVerification(email: string): Promise<any> {
    return firstValueFrom(this.http.post(this.baseApiUrl + '/parameter/inactivity/email/verification', { email }));
  }

  /**
   * Verifies the email verification code for inactivity report recipients.
   * @param email The email address being verified.
   * @param code The verification code received via email.
   * @returns {Promise<any>} A promise that resolves to the API response.
   */
  verifyInactivityEmailCode(email: string, code: string): Promise<any> {
    return firstValueFrom(this.http.post(this.baseApiUrl + '/parameter/inactivity/email/verify', { email, code }));
  }
}

/**
 * Interface for updating parameters
 * @interface IParamsUpdate
 */
export interface IParamsUpdate {
  /**
   * The key to update
   * @type {'password' | 'level' | 'frequency' | 'ad' | 'pin' | 'pinFrequency' | 'lockpatternFrequency' | 'passphraseFrequency' | 'automaticUpdate' | 'copyTimeout' | 'pinVerify' | 'deviceRestriction'}
   */
  key: 'password' | 'level' | 'frequency' | 'ad' | 'pin' | 'pinFrequency' | 'lockpatternFrequency' | 'passphraseFrequency' | 'automaticUpdate' | 'copyTimeout' | 'pinVerify' | 'deviceRestriction';
  /**
   * The new value for the key
   * @type {any}
   */
  value: any;
}
/**
 *  Interface for updating charts
 *  @interface IChartsUpdate
 */
export interface IChartsUpdate {
  /**
   * The key to update
   * @type {'seats' | 'users' | 'password' | 'passwordDetails' | 'usersActivity' | 'creditCardsDetails' | 'accessKitsDetails'}
   */
  key: 'seats' | 'users' | 'password' | 'passwordDetails' | 'usersActivity' | 'creditCardsDetails' | 'accessKitsDetails';
  /**
   * The new value for the key
   * @type {any}
   */
  value: any;
}
/**
 * Interface for system
 * @interface Isystem
 */
export interface Isystem {
  /**
   * The name of the system
   * @type {string}
   */
  name: string;
  /**
   * On first login
   * @type {boolean}
   */
  onFirstLogin: boolean;
}
